/**
 * Audit-Log — the chronicle of every conclusion and assessment so far.
 *
 * Read-only by design: the page never mutates anything, so it is guarded by
 * `triage.read` and is the auditor's natural landing point. The server
 * (`GET /api/audit-log`) reconstructs each step from the VulnerabilityEvent
 * card history, the Benachrichtigung cards and the evidence store; this page
 * only decides how it reads.
 *
 * Three layers, from overview to evidence:
 *   1. the Prüfpfad KPI row — how many decisions, how well they are reasoned,
 *      how fast they came and whether customers were informed in time;
 *   2. the stream — a day-grouped timeline ("Chronik") or one file per case
 *      ("Fallakten") with a four-step progress line;
 *   3. the aside — the current verdict mix and the people involved.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import Banner from '../components/Banner';
import Chip, { type ChipTone } from '../components/Chip';
import EmptyState from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { KpiRow, type KpiItem } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import { PageSpinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { errorMessage, fetchAuditLog } from '../lib/api';
import { fmtDate, fmtScore } from '../lib/format';
import type { AuditCase, AuditEntry, AuditLogResponse, Bewertung } from '../lib/types';

import {
  AUDIT_GROUPS,
  KIND_LABELS,
  caseSteps,
  conclusionOf,
  fmtDuration,
  groupByDay,
  groupOf,
  initials,
  isReassessment,
  komponenteLabel,
  matchesAuditSearch,
  matchesCaseSearch,
  percent,
  timeLabel,
  titleOf,
  toCsv,
  toneOf,
  verdictLabel,
  type AuditGroup,
} from './auditlog/auditModel';
import { BEWERTUNG_LABELS, parseScore, scoreTone } from './triage/eventHelpers';

import './auditlog/audit-log.css';

type View = 'chronik' | 'faelle';

/** Case-file filter (Fallakten); the KPI tiles set the same filter. */
type CaseFilter = 'alle' | 'offen' | 'neubewertet' | 'ueberfaellig';

const CASE_FILTERS: readonly { key: CaseFilter; label: string; match: (entry: AuditCase) => boolean }[] = [
  { key: 'alle', label: 'Alle', match: () => true },
  { key: 'offen', label: 'Offen', match: (entry) => entry.bewertung === 'OFFEN' },
  { key: 'neubewertet', label: 'Neubewertet', match: (entry) => entry.neubewertungen > 0 },
  { key: 'ueberfaellig', label: 'Meldung überfällig', match: (entry) => entry.meldungen.ueberfaellig > 0 },
];

/** Board tiles that switch a list filter. */
type BoardTarget = 'entscheidungen' | Exclude<CaseFilter, 'alle'>;

/** How many timeline entries render before "Weitere laden". */
const PAGE_SIZE = 40;

const VERDICT_ORDER: readonly Bewertung[] = ['OFFEN', 'BETROFFEN', 'NICHT_BETROFFEN', 'BEHOBEN'];

/** `?gruppe=` from a deep link (the dashboard's "Im Audit-Log ansehen"). */
function groupParam(value: string | null): AuditGroup {
  return AUDIT_GROUPS.some((entry) => entry.key === value) ? (value as AuditGroup) : 'alle';
}

function triageLink(cve: string | null): string {
  return cve ? `/triage?q=${encodeURIComponent(cve)}` : '/triage';
}

export function AuditLogPage(): ReactNode {
  const toast = useToast();
  const [data, setData] = useState<AuditLogResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('chronik');
  const [searchParams] = useSearchParams();
  // Deep links only seed the filters; the page does not write them back.
  const [group, setGroup] = useState<AuditGroup>(() => groupParam(searchParams.get('gruppe')));
  const [actor, setActor] = useState('');
  const [caseFilter, setCaseFilter] = useState<CaseFilter>('alle');
  const [search, setSearch] = useState(() => searchParams.get('q')?.trim() ?? '');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const requestSeq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const response = await fetchAuditLog();
      if (seq !== requestSeq.current) return;
      setData(response);
      setError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(errorMessage(err));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Filter changes start the list at the top again.
  useEffect(() => setLimit(PAGE_SIZE), [group, actor, search, view]);

  const entries = useMemo<AuditEntry[]>(() => {
    if (data === null) return [];
    return data.eintraege.filter(
      (entry) =>
        (group === 'alle' || groupOf(entry) === group) &&
        (actor === '' || entry.actor === actor) &&
        matchesAuditSearch(entry, search),
    );
  }, [data, group, actor, search]);

  const cases = useMemo<AuditCase[]>(() => {
    if (data === null) return [];
    return data.faelle.filter(
      (entry) =>
        CASE_FILTERS.find((filter) => filter.key === caseFilter)!.match(entry) &&
        (actor === '' ||
          data.eintraege.some((e) => e.event_id === entry.event_id && e.actor === actor)) &&
        matchesCaseSearch(entry, search),
    );
  }, [data, actor, search, caseFilter]);

  const caseCounts = useMemo<Record<CaseFilter, number>>(() => {
    const all = data?.faelle ?? [];
    return Object.fromEntries(
      CASE_FILTERS.map((filter) => [filter.key, all.filter(filter.match).length]),
    ) as Record<CaseFilter, number>;
  }, [data]);

  // Board tiles: „Dokumentierte Entscheidungen“ opens the „Bewertungen“ pill of
  // the chronicle, the case tiles open the case files with their filter. A
  // tile is marked while exactly its filter is active; a second click clears it.
  const plain = actor === '' && search.trim() === '';
  const boardSelected: BoardTarget | null =
    plain && view === 'chronik' && group === 'bewertungen'
      ? 'entscheidungen'
      : plain && view === 'faelle' && caseFilter !== 'alle'
        ? caseFilter
        : null;
  const onBoardSelect = (target: BoardTarget): void => {
    const active = boardSelected === target;
    setActor('');
    setSearch('');
    if (target === 'entscheidungen') {
      setView('chronik');
      setGroup(active ? 'alle' : 'bewertungen');
    } else {
      setView('faelle');
      setCaseFilter(active ? 'alle' : target);
    }
  };

  const groupCounts = useMemo<Record<AuditGroup, number>>(() => {
    const counts: Record<AuditGroup, number> = {
      alle: 0,
      bewertungen: 0,
      meldungen: 0,
      nachweise: 0,
      system: 0,
    };
    for (const entry of data?.eintraege ?? []) {
      counts.alle += 1;
      counts[groupOf(entry)] += 1;
    }
    return counts;
  }, [data]);

  const days = useMemo(() => groupByDay(entries.slice(0, limit)), [entries, limit]);

  const onExport = useCallback((): void => {
    if (entries.length === 0) return;
    const blob = new Blob([toCsv(entries)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success(`${entries.length} Einträge als CSV exportiert.`);
  }, [entries, toast]);

  const resetFilters = (): void => {
    setGroup('alle');
    setCaseFilter('alle');
    setActor('');
    setSearch('');
  };
  const filtered = group !== 'alle' || actor !== '' || search.trim() !== '';

  return (
    <>
      <PageHeader
        title="Audit-Log"
        sub={data === null ? undefined : `Stand ${fmtDate(data.generated_at)}`}
        actions={
          <button
            type="button"
            className="small"
            onClick={onExport}
            disabled={entries.length === 0}
            title="Gefilterte Chronik als CSV (Semikolon, UTF-8) herunterladen"
          >
            <Icon name="download" size={14} /> CSV exportieren
          </button>
        }
      />

      {error !== null ? (
        <Banner kind="error" sub="Prüfen Sie die Verbindung zu CMDBuild.">
          Audit-Log konnte nicht geladen werden: {error}
        </Banner>
      ) : null}
      {data !== null && !data.dokumente_verfuegbar ? (
        <Banner kind="warn">
          Der Nachweisspeicher ist nicht lesbar. Advisory- und Upload-Schritte fehlen in dieser
          Ansicht.
        </Banner>
      ) : null}

      {data === null ? (
        loading ? (
          <PageSpinner text="Audit-Log wird zusammengestellt…" />
        ) : (
          <EmptyState title="Keine Daten geladen" />
        )
      ) : (
        <>
          <AuditBoard data={data} selected={boardSelected} onSelect={onBoardSelect} />

          <div className="audit-layout">
            <section id="audit-stream" className="card audit-stream" aria-labelledby="audit-stream-title">
              <div className="audit-stream-head">
                <h2 id="audit-stream-title">
                  {view === 'chronik' ? 'Chronik' : 'Fallakten'}
                </h2>
                <div className="seg audit-view-toggle" role="group" aria-label="Ansicht wählen">
                  <button
                    type="button"
                    className={view === 'chronik' ? 'on' : undefined}
                    aria-pressed={view === 'chronik'}
                    onClick={() => setView('chronik')}
                  >
                    Chronik
                  </button>
                  <button
                    type="button"
                    className={view === 'faelle' ? 'on' : undefined}
                    aria-pressed={view === 'faelle'}
                    onClick={() => setView('faelle')}
                  >
                    Fallakten
                  </button>
                </div>
              </div>

              <div className="audit-filterbar">
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.currentTarget.value)}
                  placeholder="CVE, Komponente, Person, Kunde oder Begründung…"
                  aria-label="Audit-Log durchsuchen"
                />
                <select
                  value={actor}
                  onChange={(event) => setActor(event.currentTarget.value)}
                  aria-label="Nach Person filtern"
                >
                  <option value="">Alle Personen</option>
                  {data.kennzahlen.bewerter.map((person) => (
                    <option key={person.name} value={person.name}>
                      {person.name}
                    </option>
                  ))}
                </select>
              </div>

              {view === 'chronik' ? (
                <div className="audit-groups" role="group" aria-label="Nach Art filtern">
                  {AUDIT_GROUPS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className={group === item.key ? 'audit-group on' : 'audit-group'}
                      aria-pressed={group === item.key}
                      onClick={() => setGroup(item.key)}
                    >
                      {item.label}
                      <Chip>{groupCounts[item.key]}</Chip>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="audit-groups" role="group" aria-label="Fallakten filtern">
                  {CASE_FILTERS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className={caseFilter === item.key ? 'audit-group on' : 'audit-group'}
                      aria-pressed={caseFilter === item.key}
                      onClick={() => setCaseFilter(item.key)}
                    >
                      {item.label}
                      <Chip>{caseCounts[item.key]}</Chip>
                    </button>
                  ))}
                </div>
              )}

              <div className="audit-stream-body" aria-live="polite" aria-busy={loading}>
                {view === 'chronik' ? (
                  entries.length === 0 ? (
                    <NoEntries filtered={filtered} onReset={resetFilters} />
                  ) : (
                    <>
                      {days.map((day) => (
                        <section className="audit-day" key={day.key} aria-label={day.label}>
                          <h3 className="audit-day-label">
                            <span>{day.label}</span>
                            <span className="audit-day-count">{day.entries.length}</span>
                          </h3>
                          <ol className="audit-timeline">
                            {day.entries.map((entry) => (
                              <TimelineItem key={entry.id} entry={entry} />
                            ))}
                          </ol>
                        </section>
                      ))}
                      {entries.length > limit ? (
                        <div className="audit-more">
                          <button type="button" className="ghost" onClick={() => setLimit((n) => n + PAGE_SIZE)}>
                            Weitere {Math.min(PAGE_SIZE, entries.length - limit)} von{' '}
                            {entries.length - limit} Einträgen laden
                          </button>
                        </div>
                      ) : null}
                    </>
                  )
                ) : cases.length === 0 ? (
                  <NoEntries filtered={caseFilter !== 'alle' || actor !== '' || search.trim() !== ''} onReset={resetFilters} />
                ) : (
                  <div className="audit-cases">
                    {cases.map((entry) => (
                      <CaseCard key={entry.event_id} entry={entry} />
                    ))}
                  </div>
                )}
              </div>
            </section>

            <aside className="audit-aside" aria-label="Zusammenfassung">
              <VerdictMix data={data} />
              <People data={data} activeActor={actor} onSelect={setActor} />
            </aside>
          </div>
        </>
      )}
    </>
  );
}

/* --- Prüfpfad board --------------------------------------------------------- */

function AuditBoard({ data, selected, onSelect }: {
  data: AuditLogResponse;
  /** Tile whose filter is active. */
  selected: BoardTarget | null;
  onSelect: (target: BoardTarget) => void;
}): ReactNode {
  const k = data.kennzahlen;
  const reasoned = percent(k.begruendet, k.entscheidungen);
  const offen = k.bewertungslage.OFFEN;
  // Case tiles count case files, like the list they open; event totals go in
  // the tooltip.
  const neubewertet = data.faelle.filter((entry) => entry.neubewertungen > 0).length;
  const ueberfaellig = data.faelle.filter((entry) => entry.meldungen.ueberfaellig > 0).length;
  const toggle = (target: BoardTarget, ariaLabel: string): Partial<KpiItem> => ({
    ariaLabel,
    selected: selected === target,
    controls: 'audit-stream',
    onClick: () => onSelect(target),
  });

  // Two rows of four: the actionable counts lead each row (open assessments,
  // overdue reports), the rates and totals follow.
  const tiles: KpiItem[] = [
    {
      label: 'Offene Bewertungen',
      value: String(offen),
      tone: offen > 0 ? 'warn' : 'good',
      title: 'Fälle, deren Anwendbarkeit noch nicht entschieden ist',
      ...toggle('offen', `Offene Bewertungen: ${offen} anzeigen`),
    },
    {
      label: 'Dokumentierte Entscheidungen',
      value: k.entscheidungen,
      note: `über ${k.faelle} ${k.faelle === 1 ? 'Fall' : 'Fälle'}`,
      ...toggle('entscheidungen', `Dokumentierte Entscheidungen: ${k.entscheidungen} anzeigen`),
      title: `${k.automatisch} automatische Statuswechsel · ${k.nachweise} ${k.nachweise === 1 ? 'Nachweis' : 'Nachweise'}`,
    },
    {
      label: 'Begründungsquote',
      value: reasoned === null ? '–' : `${reasoned} %`,
      note: reasoned === null ? 'nicht anwendbar' : undefined,
      title: 'Anteil der Bewertungen mit schriftlicher Begründung',
    },
    {
      label: 'Reaktionszeit (Median)',
      value: fmtDuration(k.reaktionszeit_median_h),
      tone: 'neutral',
      title: 'Zeit vom KEV-Treffer bis zur ersten menschlichen Bewertung, Median über alle Fälle',
    },
    {
      label: 'Meldung überfällig',
      value: String(ueberfaellig),
      tone: ueberfaellig > 0 ? 'alert' : 'good',
      title: `Fälle mit noch nicht versendeten Kundenmeldungen, deren 24-h-Frist abgelaufen ist (${k.meldungen.ueberfaellig} ${k.meldungen.ueberfaellig === 1 ? 'Meldung' : 'Meldungen'})`,
      ...toggle('ueberfaellig', `Meldung überfällig: ${ueberfaellig} anzeigen`),
    },
    {
      label: 'Neubewertungen',
      value: String(neubewertet),
      tone: 'neutral',
      title: `Bereits entschiedene Fälle, deren Bewertung später geändert wurde (${k.neubewertungen} ${k.neubewertungen === 1 ? 'Neubewertung' : 'Neubewertungen'})`,
      ...toggle('neubewertet', `Neubewertungen: ${neubewertet} anzeigen`),
    },
    {
      label: 'Kunden informiert',
      value: `${k.meldungen.versendet} / ${k.meldungen.gesamt}`,
      tone:
        k.meldungen.gesamt === 0
          ? 'neutral'
          : k.meldungen.versendet >= k.meldungen.gesamt
            ? 'good'
            : 'warn',
      title: 'Als versendet gestempelte Kundenmeldungen von allen angelegten',
    },
    {
      label: 'Innerhalb 24 h versendet',
      value: `${k.meldungen.fristgerecht} / ${k.meldungen.versendet}`,
      tone: k.meldungen.versendet === 0 ? 'neutral' : k.meldungen.fristgerecht >= k.meldungen.versendet ? 'good' : 'warn',
      title: 'Versendete Kundenmeldungen, deren Versand vor der 24-h-Frist der Benachrichtigung lag',
    },
  ];

  return <KpiRow counters={tiles} label="Kennzahlen des Prüfpfads" />;
}

/* --- Timeline --------------------------------------------------------------- */

function TimelineItem({ entry }: { entry: AuditEntry }): ReactNode {
  const tone = toneOf(entry);
  const conclusion = conclusionOf(entry);
  const score = parseScore(entry.base_score);
  const reassessed = isReassessment(entry);
  const showTransition =
    entry.bewertung !== null && entry.vorher !== null && entry.vorher !== entry.bewertung;

  return (
    <li className={`audit-item tone-${tone}${reassessed ? ' reassessed' : ''}`}>
      <span className="audit-node" aria-hidden="true">
        <KindIcon kind={entry.kind} />
      </span>
      <article className="audit-entry">
        <header className="audit-entry-head">
          <Chip>{reassessed ? 'Neubewertung' : KIND_LABELS[entry.kind]}</Chip>
          <time dateTime={entry.ts ?? undefined} title={fmtDate(entry.ts)}>
            {timeLabel(entry.ts)}
          </time>
          {/* The send stamp records no user — show no actor rather than a placeholder. */}
          {entry.actor !== null || entry.actor_type === 'system' ? (
            <Actor name={entry.actor} system={entry.actor_type === 'system'} />
          ) : null}
        </header>

        <h4 className="audit-title">{titleOf(entry)}</h4>

        {showTransition ? (
          <div className="audit-transition" aria-label={`Von ${verdictLabel(entry.vorher)} zu ${verdictLabel(entry.bewertung)}`}>
            <VerdictPill value={entry.vorher} muted />
            <Icon name="arrow-right" />
            <VerdictPill value={entry.bewertung} />
          </div>
        ) : null}

        {conclusion !== null ? (
          <p className="audit-conclusion">
            <span className="audit-conclusion-label">Schluss</span>
            {conclusion}
          </p>
        ) : null}

        {entry.begruendung ? (
          <blockquote className="audit-reason">
            <span className="audit-reason-label">Begründung</span>
            {entry.begruendung}
          </blockquote>
        ) : entry.kind === 'bewertung' ? (
          <p className="audit-reason-missing">Keine Begründung hinterlegt.</p>
        ) : null}

        <footer className="audit-meta">
          {entry.kind !== 'erkannt' ? (
            <span className="audit-meta-item" title={entry.komponente.hersteller ?? undefined}>
              <ComponentIcon />
              {komponenteLabel(entry)}
            </span>
          ) : null}
          {score !== null && (entry.kind === 'erkannt' || entry.kind === 'bewertung') ? (
            <Chip tone={scoreTone(score)} title="CVSS-Basiswert">
              CVSS {fmtScore(entry.base_score)}
            </Chip>
          ) : null}
          {entry.aktiv_ausgenutzt && entry.kind === 'erkannt' ? <Chip tone="alert">KEV</Chip> : null}
          {entry.kind === 'versand' && entry.fristgerecht !== null ? (
            <Chip tone={entry.fristgerecht ? 'good' : 'warn'} title={`Frist 24 h: ${fmtDate(entry.frist)}`}>
              {entry.fristgerecht ? 'fristgerecht' : 'nach Frist'}
            </Chip>
          ) : null}
          {entry.kind === 'meldung' && entry.frist ? (
            <span className="audit-meta-item">Frist 24 h: {fmtDate(entry.frist)}</span>
          ) : null}
          {entry.kind === 'erkannt' && entry.kunden.length > 0 ? (
            <span className="audit-meta-item" title={entry.kunden.join(', ')}>
              potenziell betroffen: {entry.kunden.join(', ')}
            </span>
          ) : null}
          {entry.kind === 'meldung' && entry.kunden.length > 1 ? (
            <span className="audit-meta-item">{entry.kunden.join(', ')}</span>
          ) : null}
          {entry.detail && (entry.kind === 'versand' || entry.kind === 'advisory' || entry.kind === 'nachweis') ? (
            <span className="audit-meta-item mono">{entry.detail}</span>
          ) : null}
          <Link className="audit-open" to={triageLink(entry.cve_id)}>
            In Triage öffnen
            <Icon name="chevron-right" size={12} />
          </Link>
        </footer>
      </article>
    </li>
  );
}

const VERDICT_TONE: Readonly<Record<Bewertung, ChipTone>> = {
  OFFEN: 'warn',
  BETROFFEN: 'alert',
  NICHT_BETROFFEN: 'idle',
  BEHOBEN: 'good',
};

/** Verdict chip; `muted` is the superseded verdict of a transition. */
function VerdictPill({ value, muted = false }: { value: Bewertung | null; muted?: boolean }): ReactNode {
  return (
    <Chip tone={muted ? 'neutral' : VERDICT_TONE[value ?? 'OFFEN']} className={muted ? 'audit-verdict-prev' : undefined}>
      {verdictLabel(value)}
    </Chip>
  );
}

function Actor({ name, system }: { name: string | null; system: boolean }): ReactNode {
  const label = name ?? (system ? 'System' : 'nicht erfasst');
  return (
    <span className={system ? 'audit-actor system' : 'audit-actor'} title={system ? 'Automatischer Schritt' : 'Person'}>
      <span className="audit-avatar" aria-hidden="true">
        {system ? <SystemIcon /> : initials(label)}
      </span>
      {label}
    </span>
  );
}

/* --- Case files ------------------------------------------------------------- */

function CaseCard({ entry }: { entry: AuditCase }): ReactNode {
  const steps = caseSteps(entry);
  const score = parseScore(entry.base_score);
  return (
    <article className="audit-case">
      <header className="audit-case-head">
        <div className="audit-case-id">
          <span className="audit-case-cve mono">{entry.cve_id ?? 'CVE'}</span>
          <span className="audit-case-komp">
            {komponenteLabel(entry)}
            {entry.komponente.hersteller ? ` · ${entry.komponente.hersteller}` : ''}
          </span>
        </div>
        <VerdictPill value={entry.bewertung} />
      </header>

      <ol className="audit-steps" aria-label="Fortschritt des Falls">
        {steps.map((step) => (
          <li key={step.label} className={`audit-step ${step.state}`}>
            <span className="audit-step-dot" aria-hidden="true">
              {step.state === 'done' ? <Icon name="check" size={12} /> : null}
            </span>
            <span className="audit-step-label">{step.label}</span>
            <span className="audit-step-hint">{step.hint}</span>
          </li>
        ))}
      </ol>

      {entry.begruendung ? (
        <blockquote className="audit-reason compact" title={entry.begruendung}>
          {entry.begruendung}
        </blockquote>
      ) : null}

      <dl className="audit-case-facts">
        <div>
          <dt>Erkannt</dt>
          <dd>{fmtDate(entry.erstellt_am)}</dd>
        </div>
        <div>
          <dt>Reaktionszeit</dt>
          <dd>{fmtDuration(entry.reaktionszeit_h)}</dd>
        </div>
        <div>
          <dt>Entscheidungen</dt>
          <dd>
            {entry.entscheidungen}
            {entry.neubewertungen > 0 ? (
              <>
                {' '}
                <Chip tone="warn">{entry.neubewertungen} neu bewertet</Chip>
              </>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>Nachweise</dt>
          <dd>
            {entry.dokumente.attachments}
            {entry.dokumente.advisory ? ' · Advisory' : ''}
          </dd>
        </div>
      </dl>

      <footer className="audit-case-foot">
        {score !== null ? (
          <Chip tone={scoreTone(score)}>CVSS {fmtScore(entry.base_score)}</Chip>
        ) : null}
        <span className="audit-meta-item">
          {entry.betroffene_kunden} {entry.betroffene_kunden === 1 ? 'betroffener Kunde' : 'betroffene Kunden'}
        </span>
        <Link className="audit-open" to={triageLink(entry.cve_id)}>
          In Triage öffnen
          <Icon name="chevron-right" size={12} />
        </Link>
      </footer>
    </article>
  );
}

/* --- Aside ------------------------------------------------------------------ */

function VerdictMix({ data }: { data: AuditLogResponse }): ReactNode {
  const mix = data.kennzahlen.bewertungslage;
  const total = VERDICT_ORDER.reduce((sum, code) => sum + mix[code], 0);
  return (
    <section className="card audit-mix" aria-labelledby="audit-mix-title">
      <h2 id="audit-mix-title">Bewertungslage</h2>
      <p className="audit-aside-sub">{total} {total === 1 ? 'Fall' : 'Fälle'}</p>
      <div className="audit-mix-bar" role="img" aria-label={VERDICT_ORDER.map((c) => `${BEWERTUNG_LABELS[c]}: ${mix[c]}`).join(', ')}>
        {total === 0 ? (
          <span className="audit-mix-empty" />
        ) : (
          VERDICT_ORDER.filter((code) => mix[code] > 0).map((code) => (
            <span
              key={code}
              className={`audit-mix-seg v-${code.toLowerCase().replace('_', '-')}`}
              style={{ flexGrow: mix[code] }}
              title={`${BEWERTUNG_LABELS[code]}: ${mix[code]} (${percent(mix[code], total) ?? 0} %)`}
            />
          ))
        )}
      </div>
      <ul className="audit-mix-legend">
        {VERDICT_ORDER.map((code) => (
          <li key={code}>
            <span className={`audit-verdict-dot v-${code.toLowerCase().replace('_', '-')}`} aria-hidden="true" />
            <span>{BEWERTUNG_LABELS[code]}</span>
            <strong>{mix[code]}</strong>
            <span className="audit-mix-pct">{total > 0 ? `${percent(mix[code], total)} %` : '—'}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function People({
  data,
  activeActor,
  onSelect,
}: {
  data: AuditLogResponse;
  activeActor: string;
  onSelect: (name: string) => void;
}): ReactNode {
  const people = data.kennzahlen.bewerter;
  return (
    <section className="card audit-people" aria-labelledby="audit-people-title">
      <h2 id="audit-people-title">Beteiligte</h2>
      {people.length === 0 ? (
        <EmptyState compact title="Noch keine dokumentierten Schritte" />
      ) : (
        <ul>
          {people.map((person) => {
            const active = activeActor === person.name;
            return (
              <li key={person.name}>
                <button
                  type="button"
                  className={active ? 'audit-person on' : 'audit-person'}
                  aria-pressed={active}
                  onClick={() => onSelect(active ? '' : person.name)}
                  title={active ? 'Filter aufheben' : `Nur Schritte von ${person.name} zeigen`}
                >
                  <span className="audit-avatar lg" aria-hidden="true">
                    {initials(person.name)}
                  </span>
                  <span className="audit-person-body">
                    <span className="audit-person-name">{person.name}</span>
                    <span className="audit-person-meta">
                      {person.entscheidungen} {person.entscheidungen === 1 ? 'Bewertung' : 'Bewertungen'} ·
                      zuletzt {fmtDate(person.letzte)}
                    </span>
                  </span>
                  <strong className="audit-person-count">
                    {person.aktivitaeten}
                  </strong>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function NoEntries({ filtered, onReset }: { filtered: boolean; onReset: () => void }): ReactNode {
  return (
    <EmptyState
      title={filtered ? 'Keine Einträge für diese Filter' : 'Noch keine Einträge'}
      action={
        filtered ? (
          <button type="button" className="ghost small" onClick={onReset}>
            Filter zurücksetzen
          </button>
        ) : undefined
      }
    >
      {filtered
        ? 'Andere Suchbegriffe probieren oder die Filter zurücksetzen.'
        : 'Der KEV-Scan hat noch keine Treffer gemeldet.'}
    </EmptyState>
  );
}

/* --- Icons ------------------------------------------------------------------ */

function Svg({ children, size = 14 }: { children: ReactNode; size?: number }): ReactNode {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

function KindIcon({ kind }: { kind: AuditEntry['kind'] }): ReactNode {
  switch (kind) {
    case 'erkannt':
      return (
        <Svg>
          <circle cx="11" cy="11" r="6.5" />
          <path d="m20 20-4.2-4.2M11 8v3.5M11 14h.01" />
        </Svg>
      );
    case 'bewertung':
      return (
        <Svg>
          <path d="M12 3v18M5 7h14M5 7l-2.5 6a3 3 0 0 0 5 0L5 7ZM19 7l-2.5 6a3 3 0 0 0 5 0L19 7ZM8 21h8" />
        </Svg>
      );
    case 'wiedereroeffnet':
      return (
        <Svg>
          <path d="M4 12a8 8 0 1 0 2.3-5.6M4 4v4h4" />
        </Svg>
      );
    case 'behoben':
      return (
        <Svg>
          <path d="M12 3 4.5 6v5.5c0 4.3 3.1 8 7.5 9.5 4.4-1.5 7.5-5.2 7.5-9.5V6L12 3Z" />
          <path d="m8.8 12 2.2 2.2 4.2-4.4" />
        </Svg>
      );
    case 'meldung':
      return (
        <Svg>
          <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15L6 16ZM10 20.5h4" />
        </Svg>
      );
    case 'versand':
      return (
        <Svg>
          <path d="M21 3 10 14M21 3l-6.5 18-4.5-7-7-4.5L21 3Z" />
        </Svg>
      );
    case 'advisory':
      return (
        <Svg>
          <path d="M6 3.5h8.5L18 7v13.5H6ZM9 11h6M9 14.5h6M9 18h3.5" />
        </Svg>
      );
    case 'nachweis':
      return (
        <Svg>
          <path d="m20 11.5-8.2 8.2a5 5 0 0 1-7-7L13 4.5a3.3 3.3 0 0 1 4.7 4.7L9.5 17.4a1.7 1.7 0 0 1-2.4-2.4l7.6-7.6" />
        </Svg>
      );
  }
}

function SystemIcon(): ReactNode {
  return (
    <Svg size={12}>
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
      <circle cx="12" cy="12" r="3" />
    </Svg>
  );
}

function ComponentIcon(): ReactNode {
  return (
    <Svg size={12}>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <path d="M9 2.5V6M15 2.5V6M9 18v3.5M15 18v3.5M2.5 9H6M2.5 15H6M18 9h3.5M18 15h3.5" />
    </Svg>
  );
}

export default AuditLogPage;
